import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Phone,
  Video,
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Loader2,
  Flame,
  HeartPulse,
  Car,
  Shield,
  Camera,
  RotateCcw,
  XCircle,
} from "lucide-react";
import api from "../api/axios";
import { useGeolocation } from "../hooks/useGeolocation";
import { useMediaRecorder } from "../hooks/useMediaRecorder";
import StickyCallBar from "../components/shared/StickyCallBar";
import EmergencyLayout from "../components/layout/EmergencyLayout";
import StepProgress from "../components/ui/step-progress";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/input";
import { LoadingState } from "../components/ui/states";
import { useLanguage } from "../contexts/LanguageContext";
import { RAH_VEER_FLOW_PAUSED } from "../config/pilot";
import { saveBystanderReportId } from "../utils/essentialFields";
import { setBystanderAccessToken } from "../utils/bystanderAccess";

const MAX_VIDEO_UPLOAD_BYTES = 4.5 * 1024 * 1024;

/** The report is filed first; the video follows in the background so a large clip never blocks it. */
async function uploadReportVideo(reportId, accessToken, videoBlob) {
  if (!videoBlob || videoBlob.size === 0 || videoBlob.size > MAX_VIDEO_UPLOAD_BYTES) return;
  try {
    const video = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(videoBlob);
    });
    await api.put(
      `/emergency-report/${reportId}/video`,
      { video },
      { headers: { Authorization: `Bearer ${accessToken}` }, timeout: 120000 },
    );
  } catch (err) {
    console.warn("Report video upload failed:", err.response?.data?.error || err.message);
  }
}

const STEP_KEYS = ["emergency.step1", "emergency.step2", "emergency.step3", "emergency.step4"];

const EMERGENCY_TYPES = [
  {
    type: "medical",
    icon: HeartPulse,
    label: "Medical",
    labelHi: "चिकित्सा",
    number: "108",
    color: "bg-destructive hover:bg-destructive/90",
    desc: "Ambulance / एम्बुलेंस",
    numberDesc: "108 — Ambulance: Medical emergency, need ambulance (पूरे भारत में एम्बुलेंस)",
  },
  {
    type: "accident",
    icon: Car,
    label: "Accident",
    labelHi: "दुर्घटना",
    number: "112",
    color: "bg-destructive hover:bg-destructive/90",
    desc: "ERSS / आपातकालीन",
    numberDesc: "112 — National Emergency: Accidents, rescue, any emergency (भारत का एकीकृत आपातकाल नंबर)",
  },
  {
    type: "fire",
    icon: Flame,
    label: "Fire / Other",
    labelHi: "आग / अन्य",
    number: "112",
    color: "bg-destructive hover:bg-destructive/90",
    desc: "Fire & Rescue / अग्निशमन",
    numberDesc: "112 — Fire & Rescue: Fire, building collapse, other (आग, बचाव सेवा)",
  },
];

const ReportEmergencyPage = () => {
  const { qrId } = useParams();
  const id = qrId;
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [callInitiated, setCallInitiated] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [manualAddress, setManualAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [, setReportId] = useState(null);

  const { location, error: geoError, loading: geoLoading, requestLocation } = useGeolocation();
  const {
    recording,
    videoBlob,
    videoUrl,
    error: videoError,
    timeLeft,
    startRecording,
    stopRecording,
    resetRecording,
    streamRef,
  } = useMediaRecorder(10000);

  const videoPreviewRef = useRef(null);

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  useEffect(() => {
    if (recording && streamRef.current && videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = streamRef.current;
    }
  }, [recording, streamRef]);

  const handleEmergencyTypeSelect = (emergencyType) => {
    setSelectedType(emergencyType);
    setCallInitiated(true);
    window.location.href = `tel:${emergencyType.number}`;
    setTimeout(() => setStep(2), 2000);
  };

  const validatePhone = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length !== 10) return t("emergency.phoneValidationDigits");
    if (!/^[6-9]/.test(cleaned)) return t("emergency.phoneValidationStart");
    return "";
  };

  const handleSubmit = async () => {
    if (step === 3) {
      const err = validatePhone(phoneNumber);
      if (err) {
        setPhoneError(err);
        return;
      }
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      const hasGps =
        location?.latitude != null &&
        location?.longitude != null &&
        Number.isFinite(location.latitude) &&
        Number.isFinite(location.longitude);
      const trimmedAddress = (manualAddress || "").trim();

      const payload = {
        qrId: id,
        reporterPhone: phoneNumber.replace(/\D/g, ""),
        emergencyType: selectedType?.type || "medical",
        latitude: hasGps ? location.latitude : null,
        longitude: hasGps ? location.longitude : null,
        geoAccuracyMeters: hasGps ? location.accuracy ?? null : null,
        locationCapturedAt: hasGps ? new Date().toISOString() : null,
        geoSource: hasGps ? "gps" : trimmedAddress ? "manual" : geoError ? "denied" : "manual",
        address: trimmedAddress,
      };

      const res = await api.post("/emergency-report", payload);

      if (res.data?.bystanderAccessToken && res.data?.id) {
        setBystanderAccessToken(res.data.id, res.data.bystanderAccessToken);
        uploadReportVideo(res.data.id, res.data.bystanderAccessToken, videoBlob);
      }
      saveBystanderReportId(id, res.data?.id);

      setReportId(res.data?.id);
      setSubmitted(true);

      if (res.data?.id) {
        navigate(`/${id}/rescue?reportId=${res.data.id}`);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t("emergency.submitError");
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <LoadingState label={t("emergency.redirecting")} />;
  }

  const footerNav =
    step < 3 ? (
      <div className="bg-card border-t border-border px-3 py-3 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-lg mx-auto">
          {step === 0 && (
            <Button
              variant="emergency"
              fullWidth
              onClick={() => setStep(1)}
              disabled={!location && !manualAddress}
            >
              {t("emergency.nextSelectType")}
              <ArrowRight size={18} />
            </Button>
          )}
          {step === 2 && (
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setStep(3)}>
                {t("emergency.skip")}
              </Button>
              <Button variant="primary" fullWidth onClick={() => setStep(3)}>
                {t("emergency.next")}
              </Button>
            </div>
          )}
        </div>
      </div>
    ) : (
      <StickyCallBar />
    );

  return (
    <EmergencyLayout
      title={t("emergency.reportTitle")}
      subtitle={t("emergency.reportSubtitle")}
      showBack
      onBack={() => (step > 0 ? setStep(step - 1) : navigate(`/${id}/public`))}
      headerVariant="emergency"
      headerRight={
        <a
          href="tel:112"
          className="min-h-12 px-3 flex items-center text-sm font-bold text-white border border-white/30 rounded-lg"
        >
          112
        </a>
      }
      footer={footerNav}
      showLanguage
      contentClassName="pb-4"
    >
      <StepProgress steps={STEP_KEYS} currentStep={step} className="mb-4" />

      <div className="pb-4">
        {step === 0 && (
          <div className="animate-fade-in">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-1">{t("emergency.step1")}</h2>
            <p className="text-xs text-muted-foreground mb-4 sm:mb-6">{t("emergency.needLocationDesc")}</p>

            <div className="bg-card rounded-lg border border-border shadow-card shadow-sm p-3 mb-3">
              {geoLoading ? (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 size={20} className="animate-spin" />
                  <div>
                    <p className="text-sm font-semibold">{t("emergency.capturingLocation")}</p>
                  </div>
                </div>
              ) : location ? (
                <div>
                  <div className="flex items-center gap-2 text-emerald-600 mb-3">
                    <CheckCircle size={20} />
                    <div>
                      <p className="text-sm font-bold">{t("emergency.locationCaptured")}</p>
                    </div>
                  </div>
                  <div className="bg-muted rounded-lg p-3 text-xs text-muted-foreground font-mono">
                    <MapPin size={14} className="inline mr-1" />
                    {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 text-amber-600 mb-3">
                    <AlertTriangle size={20} />
                    <div>
                      <p className="text-sm font-bold">{geoError || t("emergency.locationUnavailable")}</p>
                    </div>
                  </div>
                  <button
                    onClick={requestLocation}
                    className="text-sm text-destructive font-semibold hover:text-destructive/90 transition-colors"
                  >
                    {t("emergency.tryAgain")}
                  </button>
                </div>
              )}
            </div>

            <div className="bg-card rounded-lg border border-border shadow-card shadow-sm p-3 mb-3">
              <label className="block text-sm font-semibold text-foreground mb-1">
                {t("emergency.manualAddressLabel")}
              </label>
              <textarea
                value={manualAddress}
                onChange={(e) => setManualAddress(e.target.value)}
                className="w-full border border-border rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary min-h-[64px] resize-none"
                placeholder={t("emergency.manualAddressPlaceholder")}
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-in">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-1">{t("emergency.step2")}</h2>
            <p className="text-xs text-muted-foreground mb-3">{t("emergency.selectTypeDesc")}</p>

            <div className="bg-muted border border-border rounded-lg p-3 mb-3 sm:mb-4">
              <p className="text-sm font-bold text-foreground mb-2">{t("emergency.indiaEmergencyNumbers")}</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li><strong>112</strong> — {t("emergency.number112Desc")}</li>
                <li><strong>108</strong> — {t("emergency.number108Desc")}</li>
                <li><strong>100</strong> — {t("emergency.number100Desc")}</li>
              </ul>
            </div>

            <div className="space-y-3 mb-6">
              {EMERGENCY_TYPES.map((etype) => {
                const Icon = etype.icon;
                const numberDescKey = etype.type === "medical" ? "emergency.number108Desc" : "emergency.number112Desc";
                const descKey = etype.type === "medical" ? "emergency.call108" : "emergency.call112";
                return (
                  <button
                    key={etype.type}
                    onClick={() => handleEmergencyTypeSelect(etype)}
                    className={`w-full flex items-center gap-3 text-white rounded-lg p-3 min-h-[56px] shadow-md transition-colors duration-150 ${etype.color}`}
                  >
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                      <Icon size={22} />
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <p className="text-base font-bold">{t(`emergency.${etype.type}`)}</p>
                      <p className="text-xs text-white/80">{t(numberDescKey)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xl font-extrabold">{etype.number}</p>
                      <p className="text-xs text-white/90 font-medium">{t(descKey)}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {callInitiated && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center animate-fade-in">
                <Phone size={20} className="text-emerald-700 mx-auto mb-2" />
                <p className="text-base font-semibold text-emerald-900">{t("emergency.callInitiatedTo")} {selectedType?.number}</p>
                <Button variant="ghost" className="mt-3" onClick={() => setStep(2)}>
                  {t("emergency.continueNext")}
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-in">
            <h2 className="text-lg font-bold text-foreground mb-1">{t("emergency.recordEvidenceOptional")}</h2>

            <div className="bg-card rounded-lg border border-border shadow-card shadow-sm p-3 mb-3">
              {videoUrl ? (
                <div>
                  <div className="flex items-center gap-2 text-emerald-600 mb-3">
                    <CheckCircle size={18} />
                    <p className="text-sm font-bold">{t("emergency.videoRecorded")}</p>
                  </div>
                  <video
                    src={videoUrl}
                    controls
                    className="w-full rounded-lg bg-black mb-3"
                    style={{ maxHeight: "140px" }}
                  />
                  <button
                    onClick={resetRecording}
                    className="flex items-center gap-1.5 text-sm text-destructive font-semibold"
                  >
                    <RotateCcw size={14} />
                    {t("emergency.recordAgain")}
                  </button>
                </div>
              ) : recording ? (
                <div className="text-center">
                  <video
                    ref={videoPreviewRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full rounded-lg bg-black mb-3"
                    style={{ maxHeight: "140px" }}
                  />
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="w-3 h-3 bg-destructive rounded-full animate-pulse" />
                    <span className="text-lg font-bold text-foreground">{timeLeft}s</span>
                  </div>
                  <button
                    onClick={stopRecording}
                    className="bg-destructive text-destructive-foreground font-bold px-6 py-2.5 rounded-lg min-h-12"
                  >
                    {t("emergency.stopRecording")}
                  </button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Camera size={48} className="text-muted-foreground mx-auto mb-3" />
                  {videoError ? (
                    <div className="flex items-center gap-2 justify-center text-destructive mb-3">
                      <XCircle size={16} />
                      <p className="text-sm">{t("emergency.cameraDenied")}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mb-3">{t("emergency.tapToStartRecording")}</p>
                  )}
                  <button
                    onClick={startRecording}
                    className="bg-destructive hover:bg-destructive/90 text-destructive-foreground font-bold px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-colors duration-150 min-h-12"
                  >
                    <Video size={18} />
                    {t("emergency.startRecording")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-in">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-1">{t("emergency.yourPhoneNumber")}</h2>
            <p className="text-xs text-muted-foreground mb-4 sm:mb-6">
              {t(RAH_VEER_FLOW_PAUSED ? "emergency.phoneRequiredDescPaused" : "emergency.phoneRequiredDesc")}
            </p>

            <div className="bg-card rounded-lg border border-border shadow-card p-4 mb-3">
              <Label>{t("emergency.phoneLabel")}</Label>
              <div className="flex items-center border border-border rounded-lg overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <span className="bg-muted px-3 py-3 text-base font-semibold text-muted-foreground border-r border-border">
                  +91
                </span>
                <input
                  type="tel"
                  inputMode="tel"
                  value={phoneNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setPhoneNumber(val);
                    setPhoneError("");
                  }}
                  className="flex-1 px-3 py-3 text-base min-h-12 outline-none"
                  placeholder={t("emergency.enterPhonePlaceholder")}
                  maxLength={10}
                />
              </div>
              {phoneError && <p className="text-sm text-destructive mt-1.5 font-medium">{phoneError}</p>}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3">
              <div className="flex items-center gap-1.5 text-amber-800">
                <Shield size={14} />
                <p className="text-xs font-semibold">
                  {t(RAH_VEER_FLOW_PAUSED ? "emergency.phonePrivacyNotePaused" : "emergency.phonePrivacyNote")}
                </p>
              </div>
            </div>

            {submitError && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm font-medium">
                {submitError}
              </div>
            )}
            <Button
              variant="emergency"
              fullWidth
              onClick={handleSubmit}
              disabled={submitting || !phoneNumber}
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {t("emergency.submitting")}
                </>
              ) : (
                <>
                  {t("emergency.submitReport")}
                  <ArrowRight size={18} />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </EmergencyLayout>
  );
};

export default ReportEmergencyPage;
